/**
 * #538 — ONE request for a PDApp's consent requirement, however many readers ask at once.
 *
 * After a page refresh every restored PDApp window asks at the same moment — its consent gate
 * (re-run several times while the viewer settles: each run's answer is dropped when the next one
 * starts, so the dialog waited for the LAST of them) and its capsule. Twenty-odd identical
 * requests then queued behind each other on a small connection pool, and the consent dialog
 * opened seconds late.
 *
 * Readers asking for the same address AS THE SAME IDENTITY (the request headers carry the active
 * org) while one is in flight share its answer. Nothing is kept once it settles: a reader that
 * asks after a consent decision always gets a fresh answer.
 */
import { getDefaultHeaders, sessionFetch, currentSessionSignal, readInSession } from '@papan/api-client'

export interface ConsentRequirementAnswer<T> {
  /** The response was 2xx (otherwise `body` is null: no principal / no manifest / forbidden). */
  ok: boolean
  body: T | null
}

const inFlight = new Map<string, Promise<ConsentRequirementAnswer<unknown>>>()

export function fetchConsentRequirement<T>(url: string): Promise<ConsentRequirementAnswer<T>> {
  const headers = getDefaultHeaders()
  const key = `${url}\n${JSON.stringify(headers)}`
  const hit = inFlight.get(key)
  if (hit) return hit as Promise<ConsentRequirementAnswer<T>>
  const run = (async (): Promise<ConsentRequirementAnswer<T>> => {
    const start = currentSessionSignal() // #1316: the body too is read in this session
    const res = await sessionFetch(url, { credentials: 'include', headers })
    if (!res.ok) return { ok: false, body: null }
    return { ok: true, body: await readInSession<T>(res, start) }
  })()
  inFlight.set(key, run)
  const clear = () => { if (inFlight.get(key) === run) inFlight.delete(key) }
  run.then(clear, clear)
  return run
}
